import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import axiosInstance from '../api';

const authHeaders = () => {
  const token = localStorage.getItem('authToken');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

const TIER_BADGE = {
  STARTER:      { labelKey: 'tierStarter',      bg: '#f3f4f6', color: '#374151' },
  PROFESSIONAL: { labelKey: 'tierProfessional', bg: '#dbeafe', color: '#1e40af' },
  ENTERPRISE:   { labelKey: 'tierEnterprise',   bg: '#f5f3ff', color: '#6b21a8' },
};

function StarRating({ value }) {
  return (
    <span>
      {[1,2,3,4,5].map(i => (
        <i key={i} className={`fas fa-star`} style={{ color: i <= Math.round(value || 0) ? '#f59e0b' : '#e5e7eb', fontSize: 11 }} />
      ))}
      <span style={{ fontSize: 11, color: '#6b7280', marginLeft: 4 }}>{Number(value || 0).toFixed(1)}</span>
    </span>
  );
}

function LeadModal({ listing, onClose }) {
  const { t } = useTranslation();
  const [form, setForm] = useState({ prospectName: '', prospectEmail: '', orgSize: '', frameworksNeeded: '', timeline: '', message: '' });
  const [saving, setSaving]   = useState(false);
  const [done, setDone]       = useState(false);
  const [err, setErr]         = useState('');

  const set = (k, v) => setForm(p => ({ ...p, [k]: v }));

  const submit = async () => {
    if (!form.prospectName || !form.prospectEmail) { setErr(t('MarketplaceDirectory.leadValidationRequired')); return; }
    setSaving(true);
    setErr('');
    try {
      await axiosInstance.post(`/marketplace/listings/${listing.slug}/leads`, form, { headers: authHeaders() });
      setDone(true);
    } catch (e) {
      setErr(e.response?.data?.message || t('MarketplaceDirectory.submissionFailed'));
    } finally {
      setSaving(false);
    }
  };

  const inp = (k, ph, type = 'text') => (
    <input type={type} value={form[k]} onChange={e => set(k, e.target.value)} placeholder={ph}
      style={{ width: '100%', padding: '8px 10px', borderRadius: 6, border: '1px solid #d1d5db', fontSize: 13, marginBottom: 12, boxSizing: 'border-box' }} />
  );

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
      <div style={{ background: '#fff', borderRadius: 14, padding: 30, width: 460, maxHeight: '90vh', overflowY: 'auto', boxShadow: '0 24px 48px rgba(0,0,0,.18)' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 20 }}>
          <span style={{ fontWeight: 700, fontSize: 16 }}>{t('MarketplaceDirectory.contactFirm', { firmName: listing.firmName })}</span>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 18, color: '#6b7280' }}>×</button>
        </div>
        {done ? (
          <div style={{ textAlign: 'center', padding: 30 }}>
            <i className="fas fa-check-circle" style={{ fontSize: 48, color: '#16a34a', marginBottom: 12, display: 'block' }} />
            <div style={{ fontWeight: 600, fontSize: 16, marginBottom: 6 }}>{t('MarketplaceDirectory.requestSent')}</div>
            <div style={{ fontSize: 13, color: '#6b7280', marginBottom: 20 }}>
              {t('MarketplaceDirectory.requestSentDetail', { firmName: listing.firmName })}
            </div>
            <button onClick={onClose} style={{ padding: '8px 20px', background: '#6366f1', color: '#fff', border: 'none', borderRadius: 6, cursor: 'pointer', fontWeight: 600 }}>
              {t('MarketplaceDirectory.close')}
            </button>
          </div>
        ) : (
          <>
            {err && <div style={{ padding: '8px 12px', background: '#fee2e2', color: '#991b1b', borderRadius: 6, fontSize: 13, marginBottom: 12 }}>{err}</div>}
            {inp('prospectName',  t('MarketplaceDirectory.phName'))}
            {inp('prospectEmail', t('MarketplaceDirectory.phEmail'), 'email')}
            {inp('orgSize',       t('MarketplaceDirectory.phOrgSize'))}
            {inp('frameworksNeeded', t('MarketplaceDirectory.phFrameworks'))}
            {inp('timeline',      t('MarketplaceDirectory.phTimeline'))}
            <textarea value={form.message} onChange={e => set('message', e.target.value)}
              placeholder={t('MarketplaceDirectory.phMessage')} rows={4}
              style={{ width: '100%', padding: '8px 10px', borderRadius: 6, border: '1px solid #d1d5db', fontSize: 13, marginBottom: 16, resize: 'vertical', boxSizing: 'border-box' }} />
            <button onClick={submit} disabled={saving}
              style={{ width: '100%', padding: 10, background: '#6366f1', color: '#fff', border: 'none', borderRadius: 6, fontWeight: 600, cursor: 'pointer', fontSize: 14 }}>
              {saving ? t('MarketplaceDirectory.sending') : t('MarketplaceDirectory.sendRequest')}
            </button>
          </>
        )}
      </div>
    </div>
  );
}

function ReviewModal({ listing, onClose }) {
  const { t } = useTranslation();
  const [rating, setRating]   = useState(5);
  const [text, setText]       = useState('');
  const [saving, setSaving]   = useState(false);
  const [done, setDone]       = useState(false);
  const [err, setErr]         = useState('');

  const submit = async () => {
    setSaving(true);
    setErr('');
    try {
      await axiosInstance.post(`/marketplace/listings/${listing.slug}/reviews`, { rating, reviewText: text }, { headers: authHeaders() });
      setDone(true);
    } catch (e) {
      setErr(e.response?.data?.message || t('MarketplaceDirectory.reviewSubmissionFailed'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
      <div style={{ background: '#fff', borderRadius: 14, padding: 30, width: 400, boxShadow: '0 24px 48px rgba(0,0,0,.18)' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 20 }}>
          <span style={{ fontWeight: 700, fontSize: 16 }}>{t('MarketplaceDirectory.reviewFirm', { firmName: listing.firmName })}</span>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 18, color: '#6b7280' }}>×</button>
        </div>
        {done ? (
          <div style={{ textAlign: 'center', padding: 20 }}>
            <i className="fas fa-check-circle" style={{ fontSize: 40, color: '#16a34a', display: 'block', marginBottom: 10 }} />
            <div style={{ fontWeight: 600 }}>{t('MarketplaceDirectory.reviewSubmitted')}</div>
            <button onClick={onClose} style={{ marginTop: 16, padding: '7px 20px', background: '#6366f1', color: '#fff', border: 'none', borderRadius: 6, cursor: 'pointer', fontWeight: 600 }}>
              {t('MarketplaceDirectory.close')}
            </button>
          </div>
        ) : (
          <>
            {err && <div style={{ padding: '8px 12px', background: '#fee2e2', color: '#991b1b', borderRadius: 6, fontSize: 13, marginBottom: 12 }}>{err}</div>}
            <div style={{ marginBottom: 14 }}>
              <div style={{ fontSize: 12, fontWeight: 600, marginBottom: 6 }}>{t('MarketplaceDirectory.rating')}</div>
              <div style={{ display: 'flex', gap: 6 }}>
                {[1,2,3,4,5].map(i => (
                  <button key={i} onClick={() => setRating(i)}
                    style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 24, color: i <= rating ? '#f59e0b' : '#e5e7eb' }}>
                    ★
                  </button>
                ))}
              </div>
            </div>
            <textarea value={text} onChange={e => setText(e.target.value)}
              placeholder={t('MarketplaceDirectory.reviewPlaceholder')} rows={4}
              style={{ width: '100%', padding: '8px 10px', borderRadius: 6, border: '1px solid #d1d5db', fontSize: 13, marginBottom: 16, resize: 'vertical', boxSizing: 'border-box' }} />
            <button onClick={submit} disabled={saving}
              style={{ width: '100%', padding: 10, background: '#f59e0b', color: '#fff', border: 'none', borderRadius: 6, fontWeight: 600, cursor: 'pointer', fontSize: 14 }}>
              {saving ? t('MarketplaceDirectory.submitting') : t('MarketplaceDirectory.submitReview')}
            </button>
          </>
        )}
      </div>
    </div>
  );
}

function ListingDetail({ slug, onBack }) {
  const { t } = useTranslation();
  const [data, setData]         = useState(null);
  const [loading, setLoading]   = useState(true);
  const [showLead, setShowLead] = useState(false);
  const [showReview, setShowReview] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const res = await axiosInstance.get(`/marketplace/listings/${slug}`, { headers: authHeaders() });
        setData(res.data);
      } catch { setData(null); }
      finally { setLoading(false); }
    })();
  }, [slug]);

  if (loading) return <div style={{ padding: 40, textAlign: 'center', color: '#6b7280' }}><i className="fas fa-spinner fa-spin" /></div>;
  if (!data)   return <div style={{ padding: 20, color: '#dc2626' }}>{t('MarketplaceDirectory.couldNotLoadListing')}</div>;

  const tb = TIER_BADGE[data.pricingTier] || TIER_BADGE.STARTER;

  return (
    <div>
      <button onClick={onBack} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#6366f1', fontSize: 13, marginBottom: 16, padding: 0, fontWeight: 600 }}>
        <i className="fas fa-arrow-left" style={{ marginRight: 6 }} /> {t('MarketplaceDirectory.backToDirectory')}
      </button>
      <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 12, padding: 28 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 16 }}>
          <div>
            <div style={{ display: 'flex', gap: 10, alignItems: 'center', marginBottom: 6 }}>
              {data.featured && <span style={{ fontSize: 11, padding: '2px 8px', borderRadius: 10, background: '#fef9c3', color: '#854d0e' }}>★ {t('MarketplaceDirectory.featured')}</span>}
              <span style={{ fontSize: 12, padding: '2px 8px', borderRadius: 10, ...tb }}>{t('MarketplaceDirectory.' + tb.labelKey)}</span>
            </div>
            <h2 style={{ margin: 0, fontSize: 22, fontWeight: 700 }}>{data.firmName}</h2>
            <div style={{ fontSize: 13, color: '#6b7280', marginTop: 4 }}>{data.partnerType}</div>
            <div style={{ marginTop: 6 }}><StarRating value={data.averageRating} /> <span style={{ fontSize: 12, color: '#9ca3af' }}>{t('MarketplaceDirectory.reviewCount', { count: data.reviewCount })}</span></div>
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <button onClick={() => setShowLead(true)}
              style={{ padding: '9px 18px', background: '#6366f1', color: '#fff', border: 'none', borderRadius: 8, fontWeight: 600, cursor: 'pointer', fontSize: 14 }}>
              {t('MarketplaceDirectory.contactPartner')}
            </button>
            <button onClick={() => setShowReview(true)}
              style={{ padding: '9px 18px', background: '#fff', color: '#6b7280', border: '1px solid #d1d5db', borderRadius: 8, fontWeight: 600, cursor: 'pointer', fontSize: 14 }}>
              {t('MarketplaceDirectory.writeReview')}
            </button>
          </div>
        </div>

        <p style={{ fontSize: 14, color: '#374151', lineHeight: 1.7, marginBottom: 20 }}>{data.bio}</p>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 12, marginBottom: 20 }}>
          {[
            { label: t('MarketplaceDirectory.regions'), val: data.regionsServed },
            { label: t('MarketplaceDirectory.languages'), val: data.languages },
            { label: t('MarketplaceDirectory.frameworks'), val: data.frameworksServed },
          ].map(item => (
            <div key={item.label} style={{ background: '#f9fafb', borderRadius: 8, padding: '10px 14px' }}>
              <div style={{ fontSize: 11, color: '#6b7280', marginBottom: 2 }}>{item.label}</div>
              <div style={{ fontSize: 13, fontWeight: 500 }}>{item.val || '—'}</div>
            </div>
          ))}
        </div>

        {(data.certifications || []).length > 0 && (
          <div style={{ marginBottom: 20 }}>
            <div style={{ fontWeight: 600, fontSize: 14, marginBottom: 8 }}>{t('MarketplaceDirectory.certifications')}</div>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              {data.certifications.map(c => (
                <span key={c.id} style={{ fontSize: 12, padding: '4px 12px', borderRadius: 20,
                  background: c.verified ? '#dcfce7' : '#f3f4f6', color: c.verified ? '#166534' : '#6b7280' }}>
                  {c.verified && <i className="fas fa-check-circle" style={{ marginRight: 4 }} />}{c.name}
                </span>
              ))}
            </div>
          </div>
        )}

        {(data.reviews || []).length > 0 && (
          <div>
            <div style={{ fontWeight: 600, fontSize: 14, marginBottom: 12 }}>{t('MarketplaceDirectory.reviews')}</div>
            {data.reviews.map(r => (
              <div key={r.id} style={{ border: '1px solid #f3f4f6', borderRadius: 8, padding: 14, marginBottom: 10 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
                  <StarRating value={r.rating} />
                  <span style={{ fontSize: 11, color: '#9ca3af' }}>{r.orgName} · {r.createdAt?.slice(0, 10)}</span>
                </div>
                {r.reviewText && <p style={{ fontSize: 13, color: '#374151', margin: '0 0 8px' }}>{r.reviewText}</p>}
                {r.partnerResponse && (
                  <div style={{ background: '#f9fafb', borderRadius: 6, padding: '8px 12px', fontSize: 12, color: '#6b7280' }}>
                    <i className="fas fa-reply" style={{ marginRight: 6 }} /><em>{r.partnerResponse}</em>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {showLead   && <LeadModal   listing={data} onClose={() => setShowLead(false)} />}
      {showReview && <ReviewModal listing={data} onClose={() => setShowReview(false)} />}
    </div>
  );
}

export default function MarketplaceDirectory() {
  const { t } = useTranslation();
  const [listings, setListings]   = useState([]);
  const [loading, setLoading]     = useState(true);
  const [error, setError]         = useState('');
  const [filter, setFilter]       = useState({ search: '', tier: '', region: '' });
  const [detailSlug, setDetailSlug] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axiosInstance.get('/marketplace/listings', { headers: authHeaders() });
      setListings(res.data || []);
    } catch {
      setError(t('MarketplaceDirectory.failedToLoad'));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => { load(); }, [load]);

  const filtered = listings.filter(l => {
    const q = filter.search.toLowerCase();
    if (q && !l.firmName?.toLowerCase().includes(q) && !l.specialties?.toLowerCase().includes(q)) return false;
    if (filter.tier && l.pricingTier !== filter.tier) return false;
    if (filter.region && !l.regionsServed?.toLowerCase().includes(filter.region.toLowerCase())) return false;
    return true;
  });

  const s = {
    page: { padding: 24, fontFamily: 'Inter, sans-serif', maxWidth: 1100 },
    h1:   { fontSize: 22, fontWeight: 700, color: '#111827', marginBottom: 4 },
    sub:  { fontSize: 13, color: '#6b7280', marginBottom: 20 },
    card: {
      background: '#fff', border: '1px solid #e5e7eb', borderRadius: 10, padding: 20,
      cursor: 'pointer', transition: 'box-shadow .2s, border-color .2s',
    },
    grid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: 16 },
  };

  if (loading) return <div style={{ padding: 40, textAlign: 'center', color: '#6b7280' }}><i className="fas fa-spinner fa-spin" /></div>;
  if (error)   return <div style={{ padding: 20, color: '#dc2626' }}>{error}</div>;
  if (detailSlug) return (
    <div style={s.page}>
      <ListingDetail slug={detailSlug} onBack={() => setDetailSlug(null)} />
    </div>
  );

  return (
    <div style={s.page}>
      <div style={s.h1}>{t('MarketplaceDirectory.title')}</div>
      <div style={s.sub}>{t('MarketplaceDirectory.subtitle')}</div>

      {/* Filters */}
      <div style={{ display: 'flex', gap: 12, marginBottom: 20, flexWrap: 'wrap' }}>
        <input
          value={filter.search}
          onChange={e => setFilter(p => ({ ...p, search: e.target.value }))}
          placeholder={t('MarketplaceDirectory.searchPlaceholder')}
          style={{ flex: '1 1 220px', padding: '8px 12px', borderRadius: 8, border: '1px solid #d1d5db', fontSize: 13 }}
        />
        <select value={filter.tier} onChange={e => setFilter(p => ({ ...p, tier: e.target.value }))}
          style={{ padding: '8px 12px', borderRadius: 8, border: '1px solid #d1d5db', fontSize: 13 }}>
          <option value="">{t('MarketplaceDirectory.allPricingTiers')}</option>
          {['STARTER', 'PROFESSIONAL', 'ENTERPRISE'].map(tier => <option key={tier} value={tier}>{t('MarketplaceDirectory.' + TIER_BADGE[tier].labelKey)}</option>)}
        </select>
        <input
          value={filter.region}
          onChange={e => setFilter(p => ({ ...p, region: e.target.value }))}
          placeholder={t('MarketplaceDirectory.regionPlaceholder')}
          style={{ padding: '8px 12px', borderRadius: 8, border: '1px solid #d1d5db', fontSize: 13 }}
        />
        <span style={{ fontSize: 13, color: '#6b7280', alignSelf: 'center' }}>
          {t('MarketplaceDirectory.resultCount', { count: filtered.length })}
        </span>
      </div>

      {filtered.length === 0 ? (
        <div style={{ textAlign: 'center', padding: 60, color: '#9ca3af' }}>{t('MarketplaceDirectory.noPartners')}</div>
      ) : (
        <div style={s.grid}>
          {filtered.map(l => {
            const tb = TIER_BADGE[l.pricingTier] || TIER_BADGE.STARTER;
            return (
              <div key={l.id}
                style={s.card}
                onClick={() => setDetailSlug(l.slug)}
                onMouseEnter={e => { e.currentTarget.style.boxShadow = '0 4px 16px rgba(0,0,0,.08)'; e.currentTarget.style.borderColor = '#a5b4fc'; }}
                onMouseLeave={e => { e.currentTarget.style.boxShadow = ''; e.currentTarget.style.borderColor = '#e5e7eb'; }}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 8 }}>
                  <div>
                    {l.featured && <span style={{ fontSize: 10, padding: '1px 7px', borderRadius: 8, background: '#fef9c3', color: '#854d0e', marginBottom: 4, display: 'inline-block' }}>★ {t('MarketplaceDirectory.featured')}</span>}
                    <div style={{ fontWeight: 700, fontSize: 15 }}>{l.firmName}</div>
                    <div style={{ fontSize: 12, color: '#6b7280' }}>{l.partnerType}</div>
                  </div>
                  <span style={{ fontSize: 11, padding: '3px 9px', borderRadius: 10, ...tb, flexShrink: 0 }}>{t('MarketplaceDirectory.' + tb.labelKey)}</span>
                </div>
                <div style={{ marginBottom: 8 }}><StarRating value={l.averageRating} /> <span style={{ fontSize: 11, color: '#9ca3af' }}>({l.reviewCount})</span></div>
                {l.specialties && <div style={{ fontSize: 12, color: '#374151', marginBottom: 8 }}>{l.specialties}</div>}
                <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                  {(l.frameworksServed || '').split(',').filter(Boolean).slice(0, 4).map(f => (
                    <span key={f} style={{ fontSize: 11, padding: '2px 8px', borderRadius: 8, background: '#e0e7ff', color: '#3730a3' }}>{f.trim()}</span>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
